import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { wedding } from "@/lib/weddingData";
import HandHeart from "./HandHeart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RSVP = () => {
  const [status, setStatus] = useState({ open: true, deadline: null });
  const [attending, setAttending] = useState(null); // null | true | false
  const [names, setNames] = useState("");
  const [allergies, setAllergies] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/rsvp/status`)
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, []);

  // Prefill existing response as the user types their name (debounced-ish)
  useEffect(() => {
    if (attending !== true) return;
    const key = names.trim();
    if (key.length < 3) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/rsvp/lookup`, {
          params: { name: key },
        });
        if (data && data.attending) {
          setAllergies(data.allergies || "");
          setNotes(data.notes || "");
          setEditing(true);
        } else {
          setEditing(false);
        }
      } catch {
        // silent
      }
    }, 450);
    return () => clearTimeout(t);
  }, [names, attending]);

  const canSubmit = useMemo(() => {
    if (attending === null) return false;
    // Names are required for both Sí and No so we always know who answered
    if (!names.trim()) return false;
    return true;
  }, [attending, names]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Rellena los campos requeridos.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, {
        attending,
        names: names.trim() || null,
        allergies: attending ? allergies.trim() || null : null,
        notes: notes.trim() || null,
      });
      setSubmitted(true);
      toast.success(
        attending
          ? "¡Gracias! Vuestra respuesta está guardada."
          : "Gracias por avisarnos."
      );
    } catch (err) {
      if (err?.response?.status === 403) {
        setStatus({ open: false, deadline: status.deadline });
        toast.error(err.response.data?.detail || "El plazo ha finalizado.");
      } else {
        toast.error("No hemos podido guardar tu respuesta. Prueba otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForNewResponse = () => {
    setAttending(null);
    setNames("");
    setAllergies("");
    setNotes("");
    setSubmitted(false);
    setEditing(false);
  };

  return (
    <section
      id="rsvp"
      data-testid="rsvp-section"
      className="relative overflow-hidden border-t border-[#c9c3b3] bg-[#E3DDCE] py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-start justify-between">
          <p className="corner-label text-[#8a8580]">(06) — Confirmación</p>
        </div>

        <div className="mt-16 grid grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-8 relative"
          >
            <HandHeart
              variant={1}
              size={52}
              rotate={-8}
              className="absolute -right-4 -top-4 hidden md:block"
            />
            <h2 className="display-xl" data-testid="rsvp-message">
              No podemos esperar
              <br />
              a celebrar con vosotros.
            </h2>
          </motion.div>
          <div className="col-span-12 md:col-span-4 md:pt-8">
            <p className="body-lead max-w-md">
              Apuntaos aquí para que podamos contar con vosotros en este día
              tan especial. Tenéis hasta el{" "}
              <span className="font-semibold text-[#0a0a0a]">
                {wedding.rsvpDeadline}
              </span>{" "}
              para confirmar vuestra asistencia.
            </p>
          </div>
        </div>

        {/* Body — either the deadline-passed notice, the success screen or the form */}
        <div className="mt-16">
          {!status.open ? (
            <div
              className="border border-[#c9c3b3] bg-[#f2ede2] p-10 md:p-14"
              data-testid="rsvp-closed"
            >
              <p className="corner-label text-[#8a8580]">Plazo cerrado</p>
              <p className="mt-4 display-md">
                El plazo para confirmar vuestra asistencia ha finalizado.
              </p>
              <p className="mt-4 body-lead max-w-2xl">
                Si necesitáis hacer algún cambio, escribidnos directamente.
              </p>
            </div>
          ) : submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="border border-[#c9c3b3] bg-[#f2ede2] p-10 md:p-14"
              data-testid="rsvp-success"
            >
              <p className="corner-label text-[#8a8580]">Guardado</p>
              <p className="mt-4 display-md">
                {attending
                  ? "¡Gracias! Contamos con vosotros."
                  : "Gracias por avisarnos, os vamos a echar de menos."}
              </p>
              <p className="mt-4 body-lead max-w-2xl">
                {attending
                  ? "Si más adelante necesitáis modificar algo, volved a este formulario con los mismos nombres — actualizaremos vuestra respuesta automáticamente."
                  : ""}
              </p>
              <button
                onClick={resetForNewResponse}
                data-testid="rsvp-new-response"
                className="mt-8 corner-label nav-underline"
              >
                Enviar otra respuesta
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="border border-[#c9c3b3] bg-[#f2ede2] p-8 md:p-12"
              data-testid="rsvp-form"
            >
              <div>
                <p className="corner-label text-[#0a0a0a]">
                  1 · ¿Podréis acompañarnos?
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {[
                    { value: true, label: "Sí, allí estaremos" },
                    { value: false, label: "No podremos ir" },
                  ].map((opt) => {
                    const active = attending === opt.value;
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => setAttending(opt.value)}
                        data-testid={`rsvp-attend-${opt.value ? "yes" : "no"}`}
                        className={
                          "px-6 py-3 border transition-colors duration-300 corner-label " +
                          (active
                            ? "border-[#891613] bg-[#891613] text-[#f4efe3]"
                            : "border-[#c9c3b3] text-[#0a0a0a] hover:border-[#891613]")
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Names + optional message — visible for BOTH Sí and No so we always know who answered */}
              <AnimatePresence initial={false}>
                {attending !== null && (
                  <motion.div
                    key="common-fields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-10">
                      <label htmlFor="rsvp-names" className="corner-label text-[#0a0a0a]">
                        {attending
                          ? "2 · Nombre(s) de las personas que asistirán *"
                          : "2 · ¿Quién nos escribe? *"}
                      </label>
                      <input
                        id="rsvp-names"
                        data-testid="rsvp-names"
                        type="text"
                        value={names}
                        onChange={(e) => setNames(e.target.value)}
                        maxLength={500}
                        required
                        placeholder={
                          attending
                            ? "Ej. Ana y Carlos + Marta (niña)"
                            : "Ej. Ana Pérez"
                        }
                        className="mt-2 block w-full border-b border-[#c9c3b3] bg-transparent py-2 text-lg outline-none transition focus:border-[#0a0a0a] placeholder:text-[#8a8580]/70"
                      />
                      {attending && (
                        <>
                          <p className="mt-3 italic-serif text-sm text-[#3a3a3a] md:text-base">
                            Por motivos de organización, la confirmación de
                            asistencia es únicamente para las personas invitadas.
                            Si tenéis cualquier duda, estaremos encantadas de
                            ayudaros.
                          </p>
                          {editing && (
                            <p className="mt-2 corner-label text-[#8a8580]">
                              Detectamos una respuesta previa con estos nombres
                              — la vamos a actualizar.
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {attending && (
                      <div className="mt-8">
                        <label
                          htmlFor="rsvp-allergies"
                          className="corner-label text-[#0a0a0a]"
                        >
                          3 · ¿Alguna alergia, intolerancia o necesidad
                          alimentaria?
                        </label>
                        <textarea
                          id="rsvp-allergies"
                          data-testid="rsvp-allergies"
                          value={allergies}
                          onChange={(e) => setAllergies(e.target.value)}
                          maxLength={500}
                          rows={2}
                          placeholder="Sin gluten, vegetariano, alergia a frutos secos…"
                          className="mt-2 block w-full resize-none border-b border-[#c9c3b3] bg-transparent py-2 text-base outline-none transition focus:border-[#0a0a0a] placeholder:text-[#8a8580]/70"
                        />
                      </div>
                    )}

                    <div className="mt-8">
                      <label
                        htmlFor="rsvp-notes"
                        className="corner-label text-[#0a0a0a]"
                      >
                        {attending
                          ? "4 · ¿Hay algo más que queráis contarnos?"
                          : "3 · ¿Queréis dejarnos algún mensaje?"}
                      </label>
                      <textarea
                        id="rsvp-notes"
                        data-testid="rsvp-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        maxLength={1000}
                        rows={3}
                        placeholder={
                          attending
                            ? "Opcional — llegamos con niños, canción para el baile, mensaje bonito…"
                            : "Opcional — un abrazo, una excusa, un mensaje bonito…"
                        }
                        className="mt-2 block w-full resize-none border-b border-[#c9c3b3] bg-transparent py-2 text-base outline-none transition focus:border-[#0a0a0a] placeholder:text-[#8a8580]/70"
                      />
                    </div>

                    {attending === false && (
                      <p className="mt-6 italic-serif text-lg text-[#3a3a3a]">
                        Nos da pena, pero gracias por decírnoslo — os vamos a
                        echar de menos.
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-10">
                <button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  data-testid="rsvp-submit"
                  className="btn-base btn-primary"
                >
                  <span>
                    {submitting ? "Enviando…" : editing ? "Actualizar respuesta" : "Enviar"}
                  </span>
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default RSVP;
